import { createContext, useContext, useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "./AuthContext";

// Guest: dish names, 3 uses/day
// Registered: dish names, unlimited
// Premium: dish names + recipe + image

const GUEST_LIMIT = 3;
const GUEST_KEY = "nextbiteGuestUsage";

function getGuestUsage() {
  try {
    const raw = JSON.parse(localStorage.getItem(GUEST_KEY) || "{}");
    const today = new Date().toDateString();
    if (raw.date !== today) return { date: today, count: 0 };
    return raw;
  } catch {
    return { date: new Date().toDateString(), count: 0 };
  }
}

function incrementGuestUsage() {
  const usage = getGuestUsage();
  usage.count += 1;
  localStorage.setItem(GUEST_KEY, JSON.stringify(usage));
  return usage.count;
}

function isGuestLocked() {
  return getGuestUsage().count >= GUEST_LIMIT;
}

const SubscriptionContext = createContext(null);

export function SubscriptionProvider({ children }) {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.uid) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    const unsub = onSnapshot(doc(db, "users", user.uid), (snap) => {
      const data = snap.data() || {};
      setSubscription({
        status: data.subscriptionStatus || "free",
        plan: data.subscriptionPlan || null,
        stripeCustomerId: data.stripeCustomerId || null,
        trialEnd: data.trialEnd || null,
      });
      setLoading(false);
    });

    return () => unsub();
  }, [user?.uid]);

  const isPremium =
    subscription?.status === "active" || subscription?.status === "trialing";
  const isRegistered = !!user;

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        loading,
        isPremium,
        isRegistered,
        isGuestLocked,
        incrementGuestUsage,
        getGuestUsage,
        GUEST_LIMIT,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  return useContext(SubscriptionContext);
}
